import { Observable, fromEvent, map, merge } from 'rxjs';

/**
 * Cross-browser fullscreen feature detection (KAN-620).
 *
 * iPhone Safari does not implement the element Fullscreen API: elements have
 * neither requestFullscreen nor webkitRequestFullscreen, and
 * document.fullscreenElement is undefined. The only fullscreen an iPhone
 * offers is the native video player, entered with
 * HTMLVideoElement.webkitEnterFullscreen() and reported through
 * webkitbeginfullscreen / webkitendfullscreen on the video. iPad before
 * iPadOS 16.4 exposes only the webkit-prefixed element API.
 *
 * Every fullscreen toggle goes through these helpers so detection lives in
 * one place and no caller throws on a missing API.
 */

type WebkitElement = Element & { webkitRequestFullscreen?: () => void };
type WebkitDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitFullscreenEnabled?: boolean;
  webkitExitFullscreen?: () => void;
};
type WebkitVideoElement = HTMLVideoElement & { webkitEnterFullscreen?: () => void };

/** The element currently fullscreen (standard or webkit-prefixed), or null. */
export function getFullscreenElement(doc: Document = document): Element | null {
  const d = doc as WebkitDocument;
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}

/** True when the browser can promote `el` to element fullscreen. False on iPhone. */
export function canRequestElementFullscreen(el: Element | null | undefined, doc: Document = document): boolean {
  if (!el) return false;
  const d = doc as WebkitDocument;
  if ((d.fullscreenEnabled ?? d.webkitFullscreenEnabled) === false) return false;
  const e = el as WebkitElement;
  return typeof e.requestFullscreen === 'function' || typeof e.webkitRequestFullscreen === 'function';
}

/** True when `el` has the standard, promise-returning requestFullscreen. */
export function hasStandardElementFullscreen(el: Element): boolean {
  return typeof el.requestFullscreen === 'function';
}

/** Requests element fullscreen via the standard API, then the webkit prefix. Never throws; failures reject. */
export function requestElementFullscreen(el: Element): Promise<void> {
  const e = el as WebkitElement;
  try {
    if (hasStandardElementFullscreen(e)) return Promise.resolve(e.requestFullscreen());
    if (typeof e.webkitRequestFullscreen === 'function') {
      e.webkitRequestFullscreen();
      return Promise.resolve();
    }
  } catch (err) {
    return Promise.reject(err);
  }
  return Promise.reject(new Error('Element fullscreen is not supported'));
}

/** Leaves element fullscreen (standard or webkit-prefixed). Never throws; failures reject. */
export function exitElementFullscreen(doc: Document = document): Promise<void> {
  const d = doc as WebkitDocument;
  try {
    if (typeof d.exitFullscreen === 'function') return Promise.resolve(d.exitFullscreen());
    if (typeof d.webkitExitFullscreen === 'function') {
      d.webkitExitFullscreen();
      return Promise.resolve();
    }
  } catch (err) {
    return Promise.reject(err);
  }
  return Promise.resolve();
}

/** True when the video can open the native (iOS) fullscreen player. */
export function canEnterVideoFullscreen(video: HTMLVideoElement | null | undefined): video is HTMLVideoElement {
  return !!video && typeof (video as WebkitVideoElement).webkitEnterFullscreen === 'function';
}

/**
 * Opens the native video fullscreen player. Returns false instead of throwing
 * when it is unsupported or refused (iOS throws InvalidStateError before the
 * video has metadata).
 */
export function enterVideoFullscreen(video: HTMLVideoElement): boolean {
  if (!canEnterVideoFullscreen(video)) return false;
  try {
    (video as WebkitVideoElement).webkitEnterFullscreen!();
    return true;
  } catch {
    return false;
  }
}

/** Emits on every standard or webkit-prefixed fullscreenchange. */
export function fullscreenChanges(doc: Document = document): Observable<Event> {
  return merge(fromEvent(doc, 'fullscreenchange'), fromEvent(doc, 'webkitfullscreenchange'));
}

/** Emits true when the video enters the native iOS player and false when it leaves. */
export function videoFullscreenChanges(video: HTMLVideoElement): Observable<boolean> {
  return merge(
    fromEvent(video, 'webkitbeginfullscreen').pipe(map(() => true)),
    fromEvent(video, 'webkitendfullscreen').pipe(map(() => false))
  );
}

type DeviceNavigator = Pick<Navigator, 'userAgent' | 'maxTouchPoints'>;

/**
 * True on iPhone and iPod touch, whose only fullscreen is the native video
 * player (KAN-635). iPad is excluded: it has element fullscreen (KAN-303).
 * An iPhone asking for the desktop site sends a Mac user agent; unlike a Mac
 * it has touch points, and unlike an iPad it has no classic MediaSource
 * (iPhone only ships ManagedMediaSource).
 */
export function isIphoneClassDevice(
  nav: DeviceNavigator = navigator,
  globals: object = globalThis
): boolean {
  if (/iPhone|iPod/.test(nav.userAgent)) return true;
  return /Macintosh/.test(nav.userAgent) && nav.maxTouchPoints > 1 && !('MediaSource' in globals);
}
